import { useAtom } from 'jotai';
import { isLoadingAtom } from './store';
import { invalidGroupUserErrorAtom, profileCompletionErrorAtom, retailUserErrorAtom, shelterMissingErrorAtom } from './atoms/authAtom';
import { Navbar } from './components/layout/Navbar';
import { Home } from './pages/home';
import Reports from './pages/reports';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom';
import '@hills-design-system/components/core.css';
import { TranslationProvider } from './context/TranslationProvider';
import { PopupManagerProvider } from './context/PopupManagerContext';
import { TranslationKeyEnum } from './types/constants';
import { useTranslation } from './hooks/useTranslation';
import { useAPI } from './hooks/useAPI';
import { useShelter } from './hooks/useShelter';
import { useAdmin } from './hooks/useAdmin';
import ErrorHandler from './components/ErrorHandler';
import ReactErrorBoundary from './components/ReactErrorBoundary';
import { useEffect } from 'react';
import Help from './pages/help';
import DigitalToolkit from './pages/digital-toolkit';
import AdminToolsPopover from './components/AdminToolsPopover/AdminToolsPopover';
import { isAdminToolsEnabled } from './utils/featureFlags';
import { InvalidSoldToModal } from './components/Modal';
import { usePageTracking } from './hooks/useAnalytics';
import Maintenance from './pages/maintenance/Maintenance';
import PopupManager from './components/PopupManager/PopupManager';

import * as Sentry from '@sentry/react';
import DynamicPage from './pages/DynamicPage';
import Account from './pages/account';
import Apply from './pages/apply';
import AccessRestrictedModal from './components/Modal/AccessRestrictedModal';
import ProfileCompletionScreen from './components/ProfileCompletionScreen/ProfileCompletionScreen';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

// Language switcher component
// function LanguageSwitcher() {
//   const { locale, changeLocaleViaUrl, t } = useTranslation();

//   return (
//     <div className="fixed top-4 left-4 z-50 bg-white shadow-md rounded-md p-2 flex gap-2">
//       <button
//         className={`px-3 py-1 rounded ${locale.startsWith('en') ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
//         onClick={() => changeLocaleViaUrl('en-US')}
//         aria-label="Switch to English"
//       >
//         EN
//       </button>
//       <button
//         className={`px-3 py-1 rounded ${locale.startsWith('fr') ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
//         onClick={() => changeLocaleViaUrl('fr-CA')}
//         aria-label="Switch to French"
//       >
//         FR
//       </button>
//       <Tooltip message={t(TranslationKeyEnum.Language)} />
//     </div>
//   );
// }

// ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Layout() {
  console.debug('[Layout] Component rendering - START');
  const { isAuthenticated, error, isInitialized, token } = useAPI();
  console.debug('[Layout] useAPI result:', { isAuthenticated, error, isInitialized });
  const { shelter } = useShelter();
  const { isAdmin, checkInProgress, checkAdminStatus } = useAdmin();
  const [isLoading] = useAtom(isLoadingAtom);
  const [shelterMissing] = useAtom(shelterMissingErrorAtom);
  const [invalidGroupUser] = useAtom(invalidGroupUserErrorAtom);
  const [retailUser] = useAtom(retailUserErrorAtom);
  const [profileCompletionError] = useAtom(profileCompletionErrorAtom);
  console.debug('[Layout] isLoading from atom:', isLoading);
  const { t } = useTranslation();
  console.debug('[Layout] Translation hook loaded');
  const location = useLocation();
  const navigate = useNavigate();
  console.debug('[Layout] Location:', location.pathname);

  // Redirect to maintenance page if maintenance mode is enabled
  useEffect(() => {
    const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
    if (isMaintenanceMode && location.pathname !== '/maintenance') {
      console.log('🔧 [FSL Router Debug] Redirecting to maintenance page');
      navigate('/maintenance', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Track page views automatically
  usePageTracking();

  // Check admin status when user is authenticated
  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      console.debug('[Layout] Checking admin status for authenticated user');
      checkAdminStatus();
    } else if (!isAuthenticated && isInitialized) {
      // Reset admin status when user logs out
      console.debug('[Layout] User logged out, resetting admin status');
      checkAdminStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInitialized, token]);

  useEffect(() => {
    console.debug('[App] Authentication state changed:', { isAuthenticated, error, isInitialized });

    // Log Gigya environment for debugging
    const gigyaApiKey = import.meta.env.VITE_GIGYA_API_KEY;
    const useMockoon = import.meta.env.VITE_USE_MOCKOON;
    console.debug('[App] Gigya config:', {
      hasApiKey: !!gigyaApiKey,
      useMockoon,
      gigyaSdkLoaded: !!(window as unknown as { gigya: unknown }).gigya,
    });
  }, [isAuthenticated, error, isInitialized]);

  // Set user properties for analytics when shelter data is available
  useEffect(() => {
    if (shelter && isAuthenticated) {
      import('./services/analytics').then(({ setUserProperties }) => {
        setUserProperties({
          shelter_name: shelter.name ?? 'unknown',
          user_type: 'shelter_user',
          is_authenticated: 'true',
        });
      });
    }
  }, [shelter, isAuthenticated]);

  useEffect(() => {
    console.log('🧭 [FSL Router Debug] Current location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });

    // 1. Only run this logic if we are on the home page root
    const isHomePage = location.pathname === '/' || location.pathname === '/home';
    if (isHomePage && location.search) {
      const params = new URLSearchParams(location.search);

      // 2. Only clear if the URL contains specific SSO/Gigya keys
      const hasAuthParams = params.has('gig_actions') || params.has('authCode');

      if (hasAuthParams) {
        console.log('🧹 Cleaning SSO tokens from Home URL');
        // This will change ".../food-shelter-love?gig_actions=..."
        // to ".../food-shelter-love" without refreshing the page
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location, navigate]);

  const handleAuthError = (errorMessage: string) => {
    console.error('Authentication failed:', errorMessage);
  };

  // Show error state if authentication is persistently failing
  const showAuthError = error?.includes('User not logged in to Gigya');
  const hasTerminalAuthError = !!shelterMissing;

  // Show loading screen until initialization is complete
  if (!isInitialized) {
    console.debug('[Layout] Showing initialization loading screen');
    return <InitializationWrapper />;
  }

  console.debug('[Layout] Initialization complete, rendering main layout');

  const isRootPath = location.pathname === '/';
  const enableAccessRestrictedModal = import.meta.env.VITE_ENABLE_ACCESS_RESTRICTED_MODAL === 'true';

  // Check if we should show the profile completion screen or Not FSL Account Modal
  const shouldShowProfileCompletion = enableAccessRestrictedModal && profileCompletionError;
  const shouldShowNotFSLAccountModal = enableAccessRestrictedModal && (retailUser || invalidGroupUser);
  

  // Logic Update:
  // We only show the Auth Error if the user is NOT on the root path
  // AND they aren't authenticated.
  const shouldBlockAccess = showAuthError && !isRootPath;
  
  const notFullAccessView = () => {
    if (shouldBlockAccess) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md">
            <h2 className="text-xl font-semibold text-red-800 mb-4">Authentication Required</h2>
            <p className="text-red-600 mb-4">
              You need to be logged in to access this application. Please ensure you're logged
              into your Hills account.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    else if(shouldShowProfileCompletion) {
      return <ProfileCompletionScreen />
    }
    else if(shouldShowNotFSLAccountModal){
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md">
            {/* Invalid Sold To & Non FSL User Modal Combined */}
            <AccessRestrictedModal isRetailAccount={retailUser} />
          </div>
        </div>
      )
    }
    else if(hasTerminalAuthError) {
      return (
        null
      );
    }
    return (
      <ReactErrorBoundary onError={(error) => console.error('[Layout] Page content error:', error)}>
        <Outlet />
      </ReactErrorBoundary>
    );
  };

  return (
    <ReactErrorBoundary onError={(error) => console.error('[Layout] React error caught:', error)}>
      <div className="min-h-screen bg-white flex flex-col  px-20">
        <ErrorHandler onError={handleAuthError} />
        <ReactErrorBoundary onError={(error) => console.error('[Layout] Navbar error:', error)}>
          {/* Only show Navbar if authenticated OR if you want it on the landing page */}
          {isAuthenticated && (
            <ReactErrorBoundary>
              <Navbar shelterName={shelter?.name} />
            </ReactErrorBoundary>
          )}
        </ReactErrorBoundary>
        <ScrollToTop />
        <main className="pb-16 pt-10 flex-grow flex flex-col gap-24">
          {notFullAccessView()}
        </main>
        {/* <Footer /> */}
        {(isLoading || !isAuthenticated) && !showAuthError && !hasTerminalAuthError && !shouldShowProfileCompletion && !shouldShowNotFSLAccountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-xl">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-center font-medium">
                {error && !showAuthError ? error : t(TranslationKeyEnum.Loading)}
              </p>
            </div>
          </div>
        )}

        {/* Admin Tools - Only visible to admins, and only where the feature flag enables them */}
        {isAdmin && !checkInProgress && isAdminToolsEnabled() && (
          <AdminToolsPopover
            onDataRefresh={() => window.location.reload()}
            onDateOverrideChanged={() => window.location.reload()}
          />
        )}

        <InvalidSoldToModal isOpen={!!shelterMissing}/>

        {/* Popup Manager - Handles all first-time visit popups */}
        <PopupManager />
      </div>
    </ReactErrorBoundary>
  );
}

// Determine basename and extract locale from AEM URL structure
const getBasename = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  console.log('🔍 [FSL Router Debug] Current hostname:', hostname);
  const pathParts = pathname.split('/').filter(Boolean);
  
  let localePrefix = '';

  if (pathParts.length > 0) {
    const potentialLocale = pathParts[0].toLowerCase();
    if (/^[a-z]{2}-[a-z]{2}$/.test(potentialLocale)) {
      localePrefix = `/${potentialLocale}`;
      console.log('✅ [FSL Router Debug] Detected locale from URL:', potentialLocale);
    }
  }

  // For fsl-frontend.hillsvet.com environment, use root path
  if (hostname.includes('fsl-frontend.hillsvet.com')) {
    console.log(
      '✅ [FSL Router Debug] Detected fsl-frontend.hillsvet.com environment, using root basename'
    );
    return '';
  }

  // For hillsvet.com/hillsvet.ca AEM environments
  if (hostname.includes('hillsvet.com') || hostname.includes('hillsvet.ca')) {
    const basename = `${localePrefix}/food-shelter-love`;
    console.log(
      '✅ [FSL Router Debug] Detected AEM environment, using basename:',
      basename
    );
    return basename;
  }

  // For local development with locale
  if (hostname === 'localhost' && localePrefix) {
    console.log('✅ [FSL Router Debug] Local development with locale, using basename:', localePrefix);
    return localePrefix;
  }

  // Default to root for all other environments
  console.log('✅ [FSL Router Debug] Using default root basename');
  return '';
};

function ConditionalRoute({ children }: Readonly<{ children: React.ReactNode }>) {
  const { isAuthenticated, isInitialized } = useAPI();
  const [invalidGroupUser] = useAtom(invalidGroupUserErrorAtom);
  const [retailUser] = useAtom(retailUserErrorAtom);
  const [shelterMissing] = useAtom(shelterMissingErrorAtom);
  const [profileCompletionError] = useAtom(profileCompletionErrorAtom);
  const hasTerminalAuthError = !!shelterMissing;
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  if (!isInitialized && !isMaintenanceMode) {
    console.debug('[Layout] Showing initialization loading screen');
    return <InitializationWrapper />;
  }

  // If in maintenance mode, skip authentication checks and render Layout so redirect can happen
  if (isMaintenanceMode) {
    return <>{children}</>;
  }

  // return isAuthenticated ? <>{children}</> : <DynamicPage slug="unauthorized-page" />;

  // Always render Layout when a terminal or recoverable auth error is present so its screens can mount
  if (isAuthenticated || hasTerminalAuthError || !!profileCompletionError || retailUser || invalidGroupUser) {
    return <>{children}</>;
  }

  return <DynamicPage slug="unauthorized-page" />;
}

function InitializationWrapper() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-center font-medium text-gray-700">{t(TranslationKeyEnum.InitializingApplication)}</p>
        <p className="mt-2 text-sm text-gray-500">
          {t(TranslationKeyEnum.SettingUpExperience)}
        </p>
      </div>
    </div>
  );
}

const basename = getBasename();
const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
const enableAccountHomePage = import.meta.env.VITE_ENABLE_ACCOUNT_HOME_PAGE === 'true';
const isLocal = window.location.hostname === 'localhost';

console.log('🔧 [FSL Router Debug] Creating router with basename:', basename);

const router = sentryCreateBrowserRouter(
  [
    ...(isMaintenanceMode
      ? [
          {
            path: '/maintenance',
            element: <Maintenance />,
          },
        ]
      : []),
    {
      path: '/',
      element: (
        <ConditionalRoute>
          <Layout />
        </ConditionalRoute>
      ),
      children: [
        // Only spread the landing route if isLocal is true
        ...(isLocal
          ? [
              {
                path: 'landing',
                element: <DynamicPage slug="unauthorized-page" />,
              },
            ]
          : []),
        {
          index: true,
          element: <Home />,
        },
        {
          path: 'home',
          element: <Home />,
        },
        {
          path: 'reports',
          element: <Reports />,
        },
        {
          path: 'resources',
          element: <DynamicPage slug="resources" authenticatedSlug="resources" header={false} />,
        },
        {
          path: 'shelter-signage',
          element: <DynamicPage slug="shelter-signage" header={false} />,
        },
        {
          path: 'help',
          element: <Help />,
        },
        ...(enableAccountHomePage
          ? [
              {
                path: 'account',
                element: <Account />,
              },
            ]
          : []),
        {
          path: 'digital-toolkit',
          element: <DigitalToolkit />,
        },
      ],
    },
    {
      path: '/apply',
      element: <Apply />,
    },
    {
      path: '/initiative-sponsorship',
      element: <DynamicPage slug="initiative-sponsorship" />,
    },
    {
      path: '/disaster-relief',
      element: <DynamicPage slug="disaster-relief-logged-out" authenticatedSlug="disaster-relief" />,
    },
    {
      path: '/shop-give-back-program',
      element: <DynamicPage slug="shop-give-back-program" authenticatedSlug="logged-in-shop-give-back-program" />,
    },
    ...['a', 'b', 'c', 'd', 'e'].map((tier) => ({
      path: `/price-proposal-tier-${tier}`,
      element: <DynamicPage slug={`price-proposal-tier-${tier}`} />,
    })),
    {
      path: '*',
      element: (() => {
        const CatchAllRoute = () => {
          const params = useParams();
          return <DynamicPage slug={params['*'] ?? ''} />;
        };
        return <CatchAllRoute />;
      })(),
    },
  ],
  {
    basename,
  }
);

function AppContent() {
  console.debug('[AppContent] Rendering router provider');
  return (
    <ReactErrorBoundary onError={(error) => console.error('[AppContent] Router error:', error)}>
      <RouterProvider router={router} />
    </ReactErrorBoundary>
  );
}

function App() {
  console.debug('[App] Rendering main app');
  return (
    <ReactErrorBoundary onError={(error) => console.error('[App] Top-level error:', error)}>
      <PopupManagerProvider>
        <TranslationProvider>
          <AppContent />
        </TranslationProvider>
      </PopupManagerProvider>
    </ReactErrorBoundary>
  );
}

export default App;
